import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  BookOpen,
  ClipboardCheck,
  GraduationCap,
  DollarSign,
  Bus,
  MessageSquare,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  UserCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Layout = ({ children, user, setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    navigate("/login");
  };

  const menuItems = [
    { path: "/", label: "Dashboard", labelSo: "Daashaboorka", icon: Home, roles: ["admin", "teacher", "super_admin", "academic_admin", "finance_admin", "transport_admin"] },
    { path: "/students", label: "Students", labelSo: "Ardayda", icon: Users, permission: "students" },
    { path: "/teachers", label: "Teachers", labelSo: "Macallimiinta", icon: UserCheck, permission: "teachers" },
    { path: "/classes", label: "Classes", labelSo: "Fasallada", icon: BookOpen, permission: "classes" },
    { path: "/attendance", label: "Attendance", labelSo: "Soo-gooshashada", icon: ClipboardCheck, permission: "attendance" },
    { path: "/assessments", label: "Assessments", labelSo: "Imtixaannada", icon: GraduationCap, permission: "assessments" },
    { path: "/finance", label: "Finance", labelSo: "Maaliyadda", icon: DollarSign, permission: "finance" },
    { path: "/transport", label: "Transport", labelSo: "Gaadiidka", icon: Bus, permission: "transport" },
    { path: "/messages", label: "Messages", labelSo: "Farriimaha", icon: MessageSquare, permission: "messages" },
    { path: "/reports", label: "Reports", labelSo: "Warbixinnada", icon: FileText, permission: "reports" },
    { path: "/settings", label: "Settings", labelSo: "Dejinta", icon: Settings, roles: ["admin", "teacher", "super_admin", "academic_admin", "finance_admin", "transport_admin"] },
  ];

  // Helper function to check if user has permission for a menu item
  const hasPermission = (item) => {
    // Dashboard and Settings are available to all users
    if (item.path === "/" || item.path === "/settings") {
      return item.roles.includes(user.role);
    }
    
    // Check permission-based access
    if (item.permission) {
      if (user.role === "super_admin") {
        return true;
      }
      return user.permissions && user.permissions[item.permission];
    }
    
    // Fallback to role-based access for backward compatibility
    return item.roles && item.roles.includes(user.role);
  };

  const filteredMenu = menuItems.filter(hasPermission);

  return (
    <div className="flex h-screen bg-[#F9F9F7]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex flex-col items-center gap-2 w-full">
              <img 
                src="/taaj-logo.png" 
                alt="Taaj Al Waqaar Logo" 
                className="w-16 h-16 object-contain"
              />
              <div className="text-center">
                <h1 className="text-lg font-bold text-[#1E624A]">Taaj Al Waqaar</h1>
                <p className="text-xs text-gray-500">Madrasa System</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden"
              data-testid="close-sidebar-btn"
            >
              <X size={24} className="text-gray-600" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">
              {filteredMenu.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase()}-link`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        isActive
                          ? "bg-[#1E624A] text-white shadow-md"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#E4C987] flex items-center justify-center text-[#1E624A] font-bold">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user.role}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="logout-btn"
              variant="outline"
              className="w-full justify-start gap-2 border-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
            >
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
              data-testid="open-sidebar-btn"
            >
              <Menu size={24} className="text-gray-600" />
            </button>
            <div className="flex-1 lg:ml-0 ml-4">
              <h2 className="text-2xl font-bold text-[#1E624A]">
                {filteredMenu.find(item => item.path === location.pathname)?.label || "Dashboard"}
              </h2>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>

      {/* Quick Add FAB (Admin only) */}
      {user.role === "admin" && (
        <div className="fixed bottom-6 right-6 z-40">
          <DropdownMenu open={showQuickAdd} onOpenChange={setShowQuickAdd}>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="quick-add-fab-btn"
                className="w-14 h-14 rounded-full bg-[#1E624A] hover:bg-[#164935] shadow-lg"
              >
                <Plus size={24} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                data-testid="quick-add-student-btn"
                onClick={() => {
                  navigate("/students");
                  setShowQuickAdd(false);
                }}
              >
                <Users size={16} className="mr-2" />
                Add Student
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="quick-add-teacher-btn"
                onClick={() => {
                  navigate("/teachers");
                  setShowQuickAdd(false);
                }}
              >
                <UserCheck size={16} className="mr-2" />
                Add Teacher
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="quick-add-expense-btn"
                onClick={() => {
                  navigate("/finance");
                  setShowQuickAdd(false);
                }}
              >
                <DollarSign size={16} className="mr-2" />
                Add Expense
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="quick-add-message-btn"
                onClick={() => {
                  navigate("/messages");
                  setShowQuickAdd(false);
                }}
              >
                <MessageSquare size={16} className="mr-2" />
                Send Message
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
